import { FC, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { MiCheckbox, MiIcon, MiModal } from "@miplatform/components";
import {
  ButtonVariants,
  Colors,
  IOrderTemplateResponse,
  PlatformModules,
} from "@miplatform/types";
import { mdiAlertOutline, mdiPlus } from "@mdi/js";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";

const LABEL_MAP = {
  [PlatformModules.MiScheduling]: "MiScheduling Orders",
  [PlatformModules.MiDispatch]: "MiDispatch Orders",
  [PlatformModules.MiInspections]: "MiInspections",
} as Record<string, string>;

export interface AddAutoTriggerOrderTemplateModalProps {
  isOpen: boolean;
  selectedTemplates: Set<string>;
  orderTemplates: IOrderTemplateResponse[];
  templatesToFlowMap: Record<string, string>;
  onClose: () => void;
  onSubmit: (data: string[]) => void;
  isLoading?: boolean;
}

interface FormValues {
  templateIds: Record<string, boolean>;
}

export const AddAutoTriggerOrderTemplateModal: FC<
  AddAutoTriggerOrderTemplateModalProps
> = ({
  isOpen,
  selectedTemplates,
  orderTemplates,
  templatesToFlowMap,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const theme = useTheme();
  const defaultValues = useMemo(() => {
    const initialMap: Record<string, boolean> = {};

    orderTemplates.forEach((template) => {
      initialMap[template.id] = selectedTemplates.has(template.id);
    });

    return { templateIds: initialMap };
  }, [orderTemplates, selectedTemplates]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues,
  });

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
    }
  }, [isOpen, defaultValues, reset]);

  const groupedOrderTemplates = useMemo(() => {
    return orderTemplates.reduce((acc, template) => {
      if (!acc[template.module]) {
        acc[template.module] = [];
      }
      acc[template.module].push(template);
      return acc;
    }, {} as Record<PlatformModules, IOrderTemplateResponse[]>);
  }, [orderTemplates]);

  const handleFormSubmit = (data: FormValues) => {
    const selectedList = Object.entries(data.templateIds)
      .filter(([_, isSelected]) => isSelected)
      .map(([templateId]) => templateId);
    onSubmit(selectedList);
  };

  return (
    <MiModal
      title="Add Orders"
      titleIcon={mdiPlus}
      leftIconColor={theme.palette.primary.main}
      open={isOpen}
      width={448}
      closeModal={onClose}
      loading={isLoading}
      description="Select with order types would you like to add to this Auto-Trigger flow."
      actions={[
        {
          name: "Cancel",
          variant: ButtonVariants.Text,
          color: Colors.Black,
          onClick: onClose,
        },
        {
          name: "Save Changes",
          variant: ButtonVariants.Contained,
          disabled: !isDirty || isLoading,
          onClick: handleSubmit(handleFormSubmit),
        },
      ]}
    >
      <Box maxHeight={294} overflow="auto">
        {Object.entries(groupedOrderTemplates).map(([module, templates]) => (
          <Box key={module}>
            <Typography
              color={theme.palette.common.black}
              fontSize={theme.fontSize.medium}
              fontWeight={theme.fontWeight.semiBold}
            >
              {LABEL_MAP[module]}
            </Typography>
            <Box display="flex" flexDirection="column">
              {templates.map((template) => (
                <Box
                  key={template.id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Controller
                    name={`templateIds.${template.id}`}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <MiCheckbox
                        value={!!value}
                        handleChange={() => onChange(!value)}
                        label={template.name}
                      />
                    )}
                  />
                  {templatesToFlowMap[template.id] && (
                    <Tooltip
                      title={`This order is currently involved in the ${
                        templatesToFlowMap[template.id]
                      }`}
                      arrow
                      placement="right"
                      slotProps={{
                        popper: {
                          sx: {
                            "& .MuiTooltip-tooltip": {
                              backgroundColor: theme.palette.warning.accent,
                              color: theme.palette.common.white,
                              padding: theme.spacing(1),
                            },
                            "& .MuiTooltip-arrow": {
                              color: theme.palette.warning.accent,
                            },
                          },
                        },
                      }}
                    >
                      <MiIcon
                        path={mdiAlertOutline}
                        size={0.8}
                        color={theme.palette.warning.accent}
                      />
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </MiModal>
  );
};
